/** Normalize API date strings (YYYY-MM-DD or YYYY/MM/DD) for comparisons */
import { canViewAccountScope, ACCOUNT_MEGELIN, getTlVisibleLogAccounts, normalizeAccount } from '../constants/accountOptions';
export function normalizeDate(value) {
  if (!value) return '';
  return String(value).trim().replace(/\//g, '-').slice(0, 10);
}

/** Parse shift start time as minutes from midnight (e.g. "13:30" → 810) */
function parseShiftStartMinutes(shift) {
  const startPart = String(shift).split('-')[0]?.trim() || shift;
  const [h, m] = startPart.split(':').map((v) => parseInt(v, 10));
  if (Number.isNaN(h)) return null;
  return h * 60 + (Number.isNaN(m) ? 0 : m);
}

const DAY_SHIFT_START_MIN = 6 * 60;
const DAY_SHIFT_END_MIN = 12 * 60 + 30;
const NIGHT_SHIFT_LATE_START_MIN = 13 * 60;
const NIGHT_SHIFT_EARLY_END_MIN = 5 * 60 + 30;

/** Day shift starts 06:00–12:30. Night: 13:00–05:30 or explicit "night" label */
export function matchesShiftFilter(shift, filter) {
  if (!filter) return true;
  if (!shift) return false;

  const normalized = String(shift).toLowerCase();
  const startMinutes = parseShiftStartMinutes(shift);

  if (filter === 'Day Shift') {
    if (normalized.includes('night')) return false;
    if (startMinutes === null) return normalized.includes('day');
    return startMinutes >= DAY_SHIFT_START_MIN && startMinutes <= DAY_SHIFT_END_MIN;
  }

  if (filter === 'Night Shift') {
    if (normalized.includes('night')) return true;
    if (startMinutes === null) return false;
    return startMinutes >= NIGHT_SHIFT_LATE_START_MIN || startMinutes <= NIGHT_SHIFT_EARLY_END_MIN;
  }

  return true;
}

export function getRegisterAddedBy(user) {
  if (!user) return 'admin';
  if (user.role === 'admin') return 'admin';
  return user.username || user.name || 'unknown';
}

export function getFirstName(fullName) {
  if (!fullName) return '';
  return String(fullName).trim().split(/\s+/)[0];
}

export function isAdminUser(user) {
  return String(user?.role || '').toLowerCase() === 'admin';
}

export function isTeamLeaderUser(user) {
  if (!user || isAdminUser(user)) return false;
  const role = String(user.role || '').toLowerCase();
  return role.includes('leader') || Boolean(user.username) || Boolean(user.name);
}

export function isSeniorTeamLeaderUser(user) {
  if (!user || isAdminUser(user)) return false;
  return String(user.role || '').toLowerCase().includes('senior');
}

export function isEmployeePortalUser(user) {
  return String(user?.role || '').toLowerCase() === 'employee';
}

export function canManageEmployeeActions(user) {
  if (isEmployeePortalUser(user)) return false;
  return isAdminUser(user) || isTeamLeaderUser(user);
}

export function canViewAccountLogs(user, account) {
  if (isEmployeePortalUser(user)) return false;
  if (isAdminUser(user)) return true;
  if (!isTeamLeaderUser(user)) return false;

  const targetAccount = normalizeAccount(account);
  const visibleAccounts = getTlVisibleLogAccounts(user?.account);

  if (visibleAccounts.includes(targetAccount)) return true;

  // Senior Team Leader can always open MEGELIN logs even when scoped to DREAME only.
  if (isSeniorTeamLeaderUser(user) && targetAccount === ACCOUNT_MEGELIN) return true;

  return false;
}
